import React, { useRef, useEffect, useCallback, useState } from "react";
import MathJax from "react-mathjax2";
import { ExtendedContentButton } from "components/ExtendedContent/ExtendedContentButton";
import { ExtendedContentCanvas } from "components/ExtendedContent/ExtendedContentCanvas";
import { ExtendedFontSizeButton } from "components/ExtendedContent/ExtendedFontSizeButton";

import { ExtendedAnswersButton } from "components/ExtendedContent/ExtendedAnswersButton";
import { ExtendedPartialButton } from "components/ExtendedContent/ExtendedPartialButton";
import Snackbar from "components/Snackbar";
import {
  ExtendedContentTools,
  ExtendedContentContextMenu,
} from "components/ExtendedContent/ExtendedContentTools";
import { EventBus, EventBusType } from "events/EventBus";
import { ExtendedContentEvent } from "../ExtendedContentEvent";
import "./content.scss";
import classnames from "classnames";
import styles from "./index.module.scss";
import { Layout } from "./layout";
import Draggable from "react-draggable";
import { getOSAndBrowser } from "util/getOs";

import Button from "components/Button/index";
import { ReaderZoomType } from "constants/ReaderTools";
import { ReaderToolsEvent ,ReaderEvent} from "events/EventTypes";
import Icon from "components/Icon";
import Repository from "repositories/Repository";
import {
  ExtendedContentScrollPositionButton,
  scrollPositionButtonBehavior,
} from "../ExtendedContentScrollPositionButton";
import {
  ScrollPositionType,
  ScrollLength,
  ScrollCoordinate,
} from "constants/scrollPositionType";
import { useStore, StoreTypes } from "context";
import { GroupInteractiveExtendedContentTool } from "components/GroupInteractiveTool/CustomExtendedContentTools";
import { ExtendedPinchPanContainer } from "components/ExtendedPinchPanContainer";
import DrawArea from "components/DrawArea";
import * as types from 'constants/actionTypes';
import { ReaderToolType } from "constants/ReaderTools";

const { ExtendedResourceRepository } = Repository;

const eventBusType = EventBusType.ExtendedContent;

export const ExtendedContentType = {
  StaticHtml: "staticHtml",
  IFrame: "iframe",
  Vocabulary: "vocabulary",
  Video: "video",
  WordGrid: "wordGrid",
  WordGlyph: "wordGlyph",
  WordExample: "wordExample",
  Whiteboard: "whiteboard",
  NumberPicker: "numberPicker",
  Synopsis: "synopsis",
  MarkerWithSound: "MarkerWithSound",
  Translation: "translation",
  Hearing: "hearing",
  FlashCard: "flashCard",
  Listening: "listening",
  AboutTheAuthor: "aboutTheAuthor",
  ZhuyinLesson: "zhuyinLesson",
  ZhuyinImage: "zhuyinImage",
  ZhuyinWord: "zhuyinWord",
  WordGridSubContent: "wordGridSubContent",
  WordRadical: "wordRadical",
  AnswerInteractiveTool: "AnswerInteractiveTool",
  PaiNote: "PaiNote",
};

const FontSizeButtonWhiteList = [
  ExtendedContentType.Translation,
  ExtendedContentType.Hearing,
  ExtendedContentType.Vocabulary,
  ExtendedContentType.Listening,
  ExtendedContentType.AboutTheAuthor,
  ExtendedContentType.WordExample,
  ExtendedContentType.ZhuyinLesson,
  ExtendedContentType.WordGridSubContent,
  ExtendedContentType.WordGlyph,
  ExtendedContentType.WordRadical,
];

const AnswersButtonWhiteList = [ExtendedContentType.Listening];

const FullScreenWhiteList = [ExtendedContentType.WordGrid];

const groupInteractivity = [
  ExtendedContentType.AnswerInteractiveTool,
  ExtendedContentType.PaiNote,
];

const getCanvasSvg = (str) => {
  // 使用 "none" 來避免自動縮放造成的位移問題
  str = str.replace("<svg", '<svg preserveAspectRatio="none"');
  return str;
};

export const ExtendedContentContainer = ({
  children,
  extendedResourceStyle,
  extendedContentType,
  onClose,
  shouldSplited = false,
}) => {
  const contentView = useRef();
  const wrapperRef = useRef();
  const disablePopWindowScale = useRef(true);
  const contentRef = useRef();
  const [{ drawAreaInfo,scrollPosition },extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const [
    {
      canvasState,
      canvasHeight,
      canvasWidth,
      isContentInit,
      svgContent,
      fontSize,
      isShowAnswer,
      popWindowScale,
      toolType,
      partialInfo,
    },
    { canvasDispatch },
  ] = ExtendedContentEvent(contentView);
  const exContainerRef = useRef(null);
  const popWinStyle = children.props
    ? children.props.extendedResourceStyle
    : null;

  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  // 判斷word直書樣式，顯示縮放按鈕與否
  useEffect(() => {
    const wordStyle = extendedResourceStyle
      ? extendedResourceStyle.split("_")[0]
      : "";
    if (wordStyle === "WordSection") {
      disablePopWindowScale.current = false;
    }
  }, [extendedResourceStyle]);

  useEffect(() => {
    // 小視窗內容上下頁分段
    const div =
      svgContent && !canvasState.isActive
        ? contentView.current.children[1]
        : contentView.current.children[0];
    // console.log("---E類小視窗---ExtendedContentContainer", div);
    if (div) {
      switch (scrollPosition) {
        case ScrollPositionType.Next: {
          setLastScrollTop(div.scrollTop);
          div.scrollTop += div.offsetHeight * ScrollLength;
          setCurrentScrollTop(div.scrollTop);
          break;
        }
        case ScrollPositionType.Prev: {
          setLastScrollTop(div.scrollTop);
          div.scrollTop -= div.offsetHeight * ScrollLength;
          setCurrentScrollTop(div.scrollTop);
          break;
        }
        default: {
          scrollPositionButtonBehavior({
            currentScroll: currentScrollTop,
            lastScroll: lastScrollTop,
            coordinate: ScrollCoordinate.Y,
          });
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);

  useEffect(() => {
    if (popWinStyle && popWinStyle.match("WordSection") != null) {
      contentView.current.style.direction = "rtl";
    }
  }, [contentView, popWinStyle]);

  useEffect(() => {
    if (popWindowScale === 1) {
      wrapperRef.current.style.transform = "";
    }
  }, [popWindowScale]);

  const checkClassName = (className) => {
    switch (className) {
      case "tab":
      case "voca":
      case "list":
        return true;
      default:
        break;
    }
    return false;
  };

  const clearCanvas = useCallback((e) => {
    let tagType = e.target.tagName || "";
    let clearCanvas = false;
    switch (tagType.toLowerCase()) {
      case "div":
        const classNames = e.target.className.split("_");
        clearCanvas = checkClassName(classNames[1]);
        break;
      default:
        break;
    }
    clearCanvas &&
      EventBus.emit({
        eventBusType,
        event: ReaderToolsEvent.ClickEraseAllEvent,
      });
  }, []);

  const handleExtendedDrawAreaEvent = useCallback(({drawAreaInfo}) => {
      extendedContentDispatch({ type: types.TOGGLE_DRAW_AREA, drawAreaInfo });

      //canvasDispatch({ type: types.CANVAS_ACTIVATE })
  },[extendedContentDispatch]);


  const handleExtendedAreaZoomEvent = useCallback(({ type, rect }) => {
    if (!rect) return;
    
    // 確保座標值總是從左上到右下
    if (rect.originWidth < 0) {
      rect.originX += rect.originWidth;
      rect.originWidth = Math.abs(rect.originWidth);
    }
    if (rect.originHeight < 0) {
      rect.originY += rect.originHeight;
      rect.originHeight = Math.abs(rect.originHeight);
    }

    const container = contentView.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // 根據容器和選取區域的寬高比來決定縮放比例
    let newScale = containerHeight / rect.originHeight;
    if (containerWidth / containerHeight < rect.originWidth / rect.originHeight) {
      newScale = containerWidth / rect.originWidth;
    }

    // 限制最大縮放倍率
    const maxScale = Math.min(newScale, 8);

    // 計算中心點位置
    const centerX = rect.originX + rect.originWidth * 0.5;
    const centerY = rect.originY + rect.originHeight * 0.5;

    // 計算新的偏移量，使選取區域置中
    const newOffsetX = -(centerX * maxScale) + containerWidth / 2;
    const newOffsetY = -(centerY * maxScale) + containerHeight / 2;

    // 更新縮放狀態
    extendedContentDispatch({
      type: types.SET_SCALE_INFO,
      scale: maxScale,
      offsetX: newOffsetX,
      offsetY: newOffsetY
    });

    // 縮放完成後切換回拖曳工具
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.ClickDragEvent
    });
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.SetReaderToolTypeEvent,
      payload: {
        toolType: ReaderToolType.Drag
      }
    });

  }, [contentView, extendedContentDispatch, eventBusType]);

  const handleExtendedReaderZoomEvent = useCallback((payload) => {
    const container = contentView.current;
    if (!container) return;

    const { type } = payload;
    
    if (type === ReaderZoomType.OriginZoom) {
      // 重置縮放
      extendedContentDispatch({
        type: types.SET_SCALE_INFO,
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      });
    } else if (
      type === ReaderZoomType.LeftTop ||
      type === ReaderZoomType.RightTop ||
      type === ReaderZoomType.LeftBottom ||
      type === ReaderZoomType.RightBottom
    ) {
      // 四格放大的情況
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      let offsetX = 0;
      let offsetY = 0;

      // 根據不同的角落設定不同的位移
      switch (type) {
        case ReaderZoomType.RightTop:
          offsetX = -containerWidth;
          break;
        case ReaderZoomType.LeftBottom:
          offsetY = -containerHeight;
          break;
        case ReaderZoomType.RightBottom:
          offsetX = -containerWidth;
          offsetY = -containerHeight;
          break;
        // LeftTop 就是預設的 (0, 0)
      }

      extendedContentDispatch({
        type: types.SET_SCALE_INFO,
        scale: 2,
        offsetX,
        offsetY,
      });
    }
  }, [contentView, extendedContentDispatch]);

  const SetFourZoomIn = useCallback((payload) => {
    
  }, [contentView, eventBusType]);

  // 監聽 ExtendedContent 相關的 DrawArea 事件
  useEffect(() => {
    // 使用 ExtendedContent 專用的 EventBus 來監聽
    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderEvent.DrawAreaEvent,
      handler: handleExtendedDrawAreaEvent
    });

    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderEvent.AreaZoomEvent,
      handler: handleExtendedAreaZoomEvent
    });

    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.SetReaderZoomEvent,
      handler: handleExtendedReaderZoomEvent
    });

    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderEvent.AreaZoomEvent,
      handler: handleExtendedReaderZoomEvent
    });

    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.FourZoomIn,
      handler: SetFourZoomIn
    });

    return () => {
      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderEvent.DrawAreaEvent,
        handler: handleExtendedDrawAreaEvent
      });
      
      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderEvent.AreaZoomEvent,
        handler: handleExtendedAreaZoomEvent
      });

      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderToolsEvent.SetReaderZoomEvent,
        handler: handleExtendedReaderZoomEvent
      });

      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderEvent.AreaZoomEvent,
        handler: handleExtendedReaderZoomEvent
      });

      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderToolsEvent.FourZoomIn,
        handler: SetFourZoomIn
      });
      
    };
  }, []);

  return (
    <Draggable disabled={popWindowScale === 1 || canvasState.isActive}>
      <div
        ref={wrapperRef}
        onClick={(e) => {
          clearCanvas(e);
        }}
      >
        <div
          id="exContainer"
          className={classnames(
            'extended-content',
            styles.extendedContentContainer,
            styles[extendedContentType],
            styles[`cursor${toolType}`]
          )}
          ref={exContainerRef}
          style={{ transform: `scale(${popWindowScale})` }}
        >
          <link
            rel="stylesheet"
            type="text/css"
            href={Layout[extendedResourceStyle]}
          />
          {/* 判斷選用樣式“直書”並且作業系統為ＭＡＣ載入此樣式 */}
          {extendedResourceStyle === "WordSection" &&
          getOSAndBrowser() === "Mac" ? (
            <link
              rel="stylesheet"
              type="text/css"
              href="/layout/wordSection/wordSectionForMac.css"
            />
          ) : (
            ""
          )}

          {extendedResourceStyle === "WordSection_108_2" &&
            getOSAndBrowser() !== "Mac" && (
              <link
                rel="stylesheet"
                type="text/css"
                href="/layout/wordSection/wordSection108-2ForWin.css"
              />
            )}

          {drawAreaInfo.isDrawArea && <DrawArea drawAreaInfo={drawAreaInfo} eventBusType={eventBusType} />}
          {shouldSplited && (
            <ExtendedPinchPanContainer
              containerWidth={canvasWidth}
              containerHeight={canvasHeight}
              extendedCanvas={canvasState.canvas} // 傳遞 ExtendedContent 的 canvas 實例
            >
              <div
                ref={contentView}
                className={classnames(styles.contentView, styles.splited, fontSize, {
                  [styles.noPadding]:
                    groupInteractivity.includes(extendedContentType),
                })}
              >
                {svgContent && !canvasState.isActive && (
                  <div
                    className={styles.svgContent}
                    dangerouslySetInnerHTML={{
                      __html: getCanvasSvg(svgContent),
                    }}
                    ref={contentRef}
                  />
                )}
                <MathJax.Context
                  id="target"
                  script={`${
                    window.electron || window.ios || window.android ? "" : "/"
                  }${ExtendedResourceRepository.getMathJaxPath()}?config=TeX-MML-AM_CHTML`}
                >
                  <MathJax.Text text={children} />
                </MathJax.Context>
                {isContentInit && (
                  <ExtendedContentCanvas
                    {...{
                      canvasState,
                      canvasDispatch,
                      canvasWidth,
                      canvasHeight,
                    }}
                  />
                )}
              </div>
            </ExtendedPinchPanContainer>
          )}
          {!shouldSplited && (
            <>
              <div
                ref={contentView}
                className={classnames(styles.contentView, fontSize, {
                  [styles.noPadding]:
                    groupInteractivity.includes(extendedContentType),
                })}
              >
                {svgContent && !canvasState.isActive && (
                  <div
                    className={styles.svgContent}
                    dangerouslySetInnerHTML={{
                      __html: getCanvasSvg(svgContent),
                    }}
                    ref={contentRef}
                  />
                )}
                {/* {drawAreaInfo.isDrawArea && <DrawArea drawAreaInfo={drawAreaInfo} />} */}
                <MathJax.Context
                  id="target"
                  script={`${
                    window.electron || window.ios || window.android ? "" : "/"
                  }${ExtendedResourceRepository.getMathJaxPath()}?config=TeX-MML-AM_CHTML`}
                >
                  <MathJax.Text text={children} />
                </MathJax.Context>
                {isContentInit && (
                  <ExtendedContentCanvas
                    {...{
                      canvasState,
                      canvasDispatch,
                      canvasWidth,
                      canvasHeight,
                    }}
                  />
                )}
              </div>
            </>
          )}
          {/* <ExtendedContentButton el={children} />
          <div className={styles.extendedButtonBox}>
            {FontSizeButtonWhiteList.includes(extendedContentType) && (
              <ExtendedFontSizeButton value={fontSize} />
            )}
            {AnswersButtonWhiteList.includes(extendedContentType) && (
              <ExtendedAnswersButton value={isShowAnswer} />
            )}
          </div> */}
          {groupInteractivity.includes(extendedContentType) && (
            <GroupInteractiveExtendedContentTool
              extendedContentType={extendedContentType}
            />
          )}
          {!groupInteractivity.includes(extendedContentType) && (
            <ExtendedContentTools
              {...{
                shouldSplited,
                extendedContentType,
                eventBusType,
                canvasState,
                popWindowScale,
                disablePopWindowScale: disablePopWindowScale.current,
                onClose,
              }}
            >
              {/* {extendedContentType === "translation" && (
              <ExtendedPartialButton {...{ canvasState }} />
            )} */}
              <ExtendedContentButton el={children} />
              {FontSizeButtonWhiteList.includes(extendedContentType) &&
                !shouldSplited && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {extendedContentType !== "translation" && (
                      <ExtendedContentScrollPositionButton />
                    )}
                    <ExtendedFontSizeButton value={fontSize} />
                  </div>
                )}
              {AnswersButtonWhiteList.includes(extendedContentType) && (
                <ExtendedAnswersButton value={isShowAnswer} />
              )}
              {FullScreenWhiteList.includes(extendedContentType) && (
                <Button
                  onClick={() => {
                    EventBus.emit({
                      event: ReaderToolsEvent.SetReaderZoomEvent,
                      payload: { type: ReaderZoomType.OriginZoom },
                    });
                  }}
                  title="全螢幕"
                  className={styles.unfocus}
                >
                  <Icon name="AllScreen" />
                  <div className={styles.text}>全螢幕</div>
                </Button>
              )}
            </ExtendedContentTools>
          )}
          <ExtendedContentContextMenu {...{ eventBusType, canvasState }} />

          <Snackbar />
        </div>
      </div>
    </Draggable>
  );
};
